import { CheckCircleOutline, ExpandMore, KeyboardDoubleArrowUp, PeopleOutline } from '@mui/icons-material';
import { Box, Fab, Hidden, MenuItem, Select, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Grid2 from '@mui/material/Unstable_Grid2';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { MbButton } from '@/components/shared/atoms';
import grnSvg from '@/components/shared/image/grn-pin.svg';
import { MbDialog } from '@/components/shared/modules';
import type { MbDialogRef } from '@/components/shared/modules/MbDialog';
import type { MbListRef } from '@/components/shared/modules/MbList';
import { MbList } from '@/components/shared/modules/MbList';
import { CODE_MASTER_TYPE } from '@/constants/code-master-type';
import { SEARCH_TYPE_TODAY, SEARCH_TYPE_TOMORROW } from '@/constants/constants';
import { queryKeys } from '@/constants/query-key';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import { useGenericQuery } from '@/modules/hooks/use-query';
import { useCodeMaster } from '@/modules/hooks/useCodeMaster';
import * as paths from '@/routes/paths';
import { settingState } from '@/state/settingState';

// オファリング一覧取得APIのリクエスト項目
type FormData = {
  areaId: number;
  searchType: string;
  date: string;
  hmsFrom: string;
  hmsTo: string;
};

// 確定済みドライブ一覧項目
type DriveListInfoType = {
  areaName: string;
  driveDate: string;
  firstEstimatedPickupDatetime: string;
  firstPickupWaypointNumber: string;
  firstPickupWaypoint: string;
  lastDropoffWaypointNumber: string;
  lastDropoffWaypoint: string;
  lastEstimatedDropoffDatetime: string;
};

// オファリング一覧取得APIのレスポンス項目
export type OfferingInformation = {
  offeringList: OfferingList[];
};

export type OfferingList = {
  reservationId: string;
  requestedAppointmentAt: string;
  passemgerCount: number;
  price: number;
  reservationTimingType: string;
  allowRideShare: string;
  status: string;
  pickupWaypointId: number;
  pickupWaypointName: string;
  pickupWaypointNumber: string;
  pickupLatitude: string;
  pickupLongitude: string;
  pickupType: string;
  dropoffWaypointId: number;
  dropoffWaypointName: string;
  dropoffWaypointNumber: string;
  droppoffLatitude: string;
  droppoffLongitude: string;
  dropoffType: string;
  message: string;
};

export const Offering: React.FC = () => {
  dayjs.locale('ja');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmOrUp = useMediaQuery(theme.breakpoints.up('sm'));

  const [setting] = useRecoilState(settingState);
  const driverID = setting?.driverId || '';
  const [driveListInfo, setDriveListInfo] = useState<DriveListInfoType[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [dataShows, setDataShows] = useState(false);
  const [button1ClickFlag, setButton1ClickFlag] = useState(true);
  const [button2ClickFlag, setButton2ClickFlag] = useState(true);
  const [fetchEnable, setFetchEnable] = useState<boolean>(false);
  const [areaIdChange, setAreaIdChange] = useState<number>(0);
  const [searchInfo, setSearchInfo] = useState<FormData>({
    areaId: 0,
    searchType: '',
    date: '',
    hmsFrom: '',
    hmsTo: '23:59:59',
  });
  const [offeringList, setOfferingList] = useState<OfferingList[]>([]);
  const allowRideShareCodes = useCodeMaster(CODE_MASTER_TYPE.ALLOW_RIDE_SHARE);
  const driveCount = driveListInfo.length || 0;
  const location = useLocation();
  const { areaId, searchType } = (location.state as FormData) || {};
  const dateToday = dayjs().format('YYYY-MM-DD');
  const dateTomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');
  const listRef = useRef<MbListRef>(null);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const handleResize = useCallback(() => {
    setWindowHeight(window.innerHeight);
  }, []);

  const {
    data: driveData,
    error,
    isError,
  } = useGenericQuery<DriveListInfoType[]>(
    queryKeys.v1Drives,
    ApiNamePaths.drivesGetApi.apiName,
    ApiNamePaths.drivesGetApi.apiPath,
    {},
    {
      queryParams: {
        driverID,
        dateFrom: dateToday,
        dateTo: dateTomorrow,
      },
    },
  );

  const {
    data: offeringInfo,
    error: offError,
    isError: offIsError,
    refetch,
    isLoading,
  } = useGenericQuery<OfferingInformation>(
    queryKeys.v1Offerings,
    ApiNamePaths.offeringsGetApi.apiName,
    ApiNamePaths.offeringsGetApi.apiPath,
    { enabled: fetchEnable },
    {
      queryParams: {
        driverID,
        areaId: searchInfo?.areaId,
        date: searchInfo?.date,
        hmsFrom: searchInfo?.hmsFrom,
        hmsTo: searchInfo?.hmsTo,
      },
    },
  );

  // 相乗り可否を表示文言
  const allowRideShareValue = (status: string) => allowRideShareCodes.find((e) => e.key === status)?.value || '';

  const getDateAndDateLabel = useCallback(
    (type: string) => {
      // 今日で検索する
      let date = dayjs(dateToday);
      let hmsFromSearch = dayjs().format('HH:mm:ss');
      // 明日で検索する
      if (type === SEARCH_TYPE_TOMORROW) {
        setButton1ClickFlag(true);
        setButton2ClickFlag(false);
        date = dayjs(dateTomorrow);
        hmsFromSearch = '00:00:00';
      } else {
        setButton1ClickFlag(false);
        setButton2ClickFlag(true);
      }

      return [date.format('YYYY-MM-DD'), hmsFromSearch];
    },
    [dateToday, dateTomorrow],
  );

  const handleClick = (type: string) => {
    const [date, hmsFromToday] = getDateAndDateLabel(type);
    setErrorMessage('');
    setFetchEnable(true);
    if (
      searchInfo.areaId === areaIdChange &&
      searchInfo.date === date &&
      searchInfo.searchType === type &&
      searchInfo.hmsFrom === hmsFromToday
    ) {
      void refetch();
    } else {
      setSearchInfo((item) => ({ ...item, areaId: areaIdChange, date, searchType: type, hmsFrom: hmsFromToday }));
    }

    setDataShows(true);
    listRef.current?.clearChecked();
  };

  const handleChange = (e: { target: { value: string } }) => {
    setAreaIdChange(Number(e.target.value));
    setButton1ClickFlag(true);
    setButton2ClickFlag(true);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    // オファリング一覧取得APIのレスポンス
    if (!isLoading) {
      if (offIsError) {
        setErrorMessage(offError?.message);
      } else if (offeringInfo && offeringInfo.offeringList.length > 0) {
        const offerings = offeringInfo.offeringList?.sort((a, b) =>
          a.requestedAppointmentAt.localeCompare(b.requestedAppointmentAt),
        );
        setOfferingList(offerings);
      } else {
        setOfferingList(offeringInfo?.offeringList || []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offError, offIsError, isLoading, offeringInfo]);

  useEffect(() => {
    if (areaId && searchType) {
      // 入力チェック
      if (!areaId || areaId === 0) {
        // エリアプルダウンに値がセットされているかチェック
        setErrorMessage(t('offering.checkMessage'));

        return;
      }

      // オファリング一覧取得APIリクエスト
      const [date, hmsFromToday] = getDateAndDateLabel(searchType);
      setFetchEnable(true);
      setSearchInfo((item) => ({ ...item, areaId: Number(areaId), searchType, date, hmsFrom: hmsFromToday }));
      setDataShows(true);
      setAreaIdChange(Number(areaId));
    }
  }, [areaId, getDateAndDateLabel, searchType, t]);

  useEffect(() => {
    // ドライブ一覧取得APIをリクエスト
    if (isError) {
      setErrorMessage(error?.message);

      return;
    }
    if (driveData) {
      setDriveListInfo(driveData);
    }
  }, [driveData, error, isError]);

  const listItemRender = (props: OfferingList) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 22, color: '#1f2937' }}>
          {dayjs(props.requestedAppointmentAt).format('M/D(ddd) HH:mm')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#374151' }}>
          <PeopleOutline sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: 16 }}>{`${props.passemgerCount}名`}</Typography>
        </Box>
        <Box sx={{ flex: '1 1 auto', display: { xs: 'none', md: 'block' } }} />
        <Typography
          variant="caption"
          fontWeight="bold"
          bgcolor={
            props.allowRideShare === '1'
              ? alpha(theme.palette.warning.main, 0.25)
              : alpha(theme.palette.primary.main, 0.15)
          }
          color={props.allowRideShare === '1' ? '#92400e' : 'text.primary'}
          sx={{ px: 1, py: '3px', borderRadius: 1 }}
        >
          {allowRideShareValue(props.allowRideShare)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 0.75,
                borderRadius: 0.75,
                bgcolor: '#f0f9ff',
                color: '#0369a1',
                fontWeight: 700,
                width: 'fit-content',
              }}
            >
              <Typography sx={{ fontSize: 14, lineHeight: '19px' }}>{props.pickupWaypointNumber}</Typography>
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }} noWrap>
              {props.pickupWaypointName}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 0.75,
                borderRadius: 0.75,
                bgcolor: '#f0fdf4',
                color: '#15803d',
                fontWeight: 700,
                width: 'fit-content',
              }}
            >
              <Typography sx={{ fontSize: 14, lineHeight: '19px' }}>{props.dropoffWaypointNumber}</Typography>
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }} noWrap>
              {props.dropoffWaypointName}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const [scrollTop, setScrollTop] = useState(0);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollTop(scrollRef.current?.scrollTop || 0);
    };
    const htmlDiv = scrollRef.current;
    htmlDiv?.addEventListener('scroll', handleScroll);

    return () => htmlDiv?.removeEventListener('scroll', handleScroll);
  }, []);

  const [popUpDatas, setPopUpDatas] = useState<OfferingList[]>([]);
  const [popUpData, setPopUpData] = useState<OfferingList | null>();
  const dialogRef = useRef<MbDialogRef>(null);
  const [isDriveListView, setIsDriveListView] = useState(false);

  // 乗降地点確認画面に遷移
  const navigateWaypointConfirm = async () => {
    const selecteKeys = listRef.current?.getChecked();
    const selectedData = offeringList.filter((item) => selecteKeys?.includes(item.reservationId));

    navigate(paths.waypointConfirm, {
      state: {
        checkedOfferingList: selectedData,
        areaId: searchInfo.areaId,
        searchType: searchInfo.searchType,
      },
    });
  };

  // 確認ボタン押下時のチェック
  const handleCheck = async () => {
    const selecteKeys = listRef.current?.getChecked();
    if (!selecteKeys || selecteKeys.length === 0) {
      // 受諾チェックボックスが何も選択されていないかチェック
      setErrorMessage(t('offering.checkMessage1'));

      return;
    }

    // 伝達事項
    const tempPopupData = offeringList.filter(
      (item) => selecteKeys?.includes(item.reservationId) && item.message !== null && item.message.trim() !== '',
    );
    if (tempPopupData.length > 0) {
      setPopUpDatas(tempPopupData);
      setPopUpData(tempPopupData[0]);
      await dialogRef.current?.show();
    } else {
      // 乗降地点確認画面に遷移
      void navigateWaypointConfirm();
    }
  };

  const handleOpenDriveList = () => {
    setIsDriveListView(true);
    setErrorMessage('');
  };

  const handleBackFromDriveList = () => {
    setIsDriveListView(false);
    setErrorMessage('');
    setDataShows(false);
    setOfferingList([]);
    setFetchEnable(false);
    setButton1ClickFlag(true);
    setButton2ClickFlag(true);
    listRef.current?.clearChecked();
  };

  return (
    <Box>
      <Grid2 ref={scrollRef} container overflow="auto" maxHeight={`calc(${windowHeight}px - 56px)`}>
        <Grid2
          sm={8}
          xs={12}
          smOffset={2}
          sx={{
            mt: { sm: '56px', md: driveCount > 0 ? '70px' : '110px', xs: driveCount > 0 ? '56px' : '110px' },
          }}
          padding={2.5}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            {isDriveListView ? (
              isSmOrUp && (
                <Box sx={{ ml: 'auto' }}>
                  <MbButton
                    btnType="secondary"
                    disabled={isLoading}
                    onClick={() => {
                      handleBackFromDriveList();
                    }}
                    sx={{
                      bgcolor: 'white',
                      color: '#64748b',
                      border: '1px solid #d1d5db',
                      boxShadow: '0px 6px 0px rgba(0,0,0,0.12)',
                      '&:hover': { bgcolor: '#f8fafc', border: '1px solid #d1d5db' },
                    }}
                  >
                    {t('offering.back')}
                  </MbButton>
                </Box>
              )
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <img src={grnSvg} alt="" style={{ width: '28px', height: '28px' }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#64748b' }}>
                    {t('offering.areaId')}
                  </Typography>
                </Box>
                <MbButton
                  btnType="primary"
                  onClick={() => {
                    handleOpenDriveList();
                  }}
                  disabled={isLoading || driveCount === 0}
                  sx={
                    isLoading || driveCount === 0
                      ? undefined
                      : {
                          bgcolor: '#003ecf',
                          '&:hover': { bgcolor: '#0030a0' },
                        }
                  }
                >
                  {t('offering.listTitle')}
                </MbButton>
              </>
            )}
          </Box>

          {!isDriveListView && (
            <Box sx={{ mt: 1.5 }}>
              <Select
                fullWidth
                defaultValue=""
                displayEmpty
                value={areaIdChange === 0 ? '' : String(areaIdChange)}
                onChange={handleChange}
                IconComponent={ExpandMore}
                sx={{
                  height: 72,
                  borderRadius: 4,
                  bgcolor: 'white',
                  border: '2px solid #cbd5e1',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 20,
                    color: '#1e293b',
                    px: 3.5,
                  },
                  '& fieldset': { border: 'none' },
                }}
              >
                <MenuItem value="">
                  <Typography sx={{ color: '#1e293b' }}>{t('offering.areaIdSelect')}</Typography>
                </MenuItem>
                {(setting?.areaList || []).map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {!isDriveListView && (
            <Grid2 mt={1.5} display="flex" justifyContent="center" gap={2} mb={1}>
              <button
                type="button"
                className="offering-date-button"
                disabled={areaIdChange === 0 || !button1ClickFlag || isLoading}
                onClick={() => {
                  handleClick(SEARCH_TYPE_TODAY);
                }}
              >
                {t('offering.dateButton1')}
              </button>
              <button
                type="button"
                className="offering-date-button"
                disabled={areaIdChange === 0 || !button2ClickFlag || isLoading}
                onClick={() => {
                  handleClick(SEARCH_TYPE_TOMORROW);
                }}
              >
                {t('offering.dateButton2')}
              </button>
            </Grid2>
          )}

          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mt: 1, mb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#64748b' }}>
              {isDriveListView ? t('offering.listTitle') : t('offering.searchResultTitle')}
            </Typography>
            {!isDriveListView && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  bgcolor: '#fff4e5',
                  borderRadius: 2,
                }}
              >
                <CheckCircleOutline sx={{ fontSize: 20, color: '#92400e' }} />
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#92400e' }}>
                  {t('offering.searchTitle3')}
                </Typography>
              </Box>
            )}
          </Box>

          {isDriveListView ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {(driveListInfo || []).map((d) => (
                <Box
                  key={`${d.driveDate}-${d.firstEstimatedPickupDatetime}-${d.firstPickupWaypointNumber}-${d.lastDropoffWaypointNumber}`}
                  sx={{
                    border: '1px solid #cbd5e1',
                    borderRadius: 2,
                    bgcolor: 'white',
                    my: 1,
                    px: 2,
                    py: 2,
                  }}
                >
                  <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#1f2937' }}>
                    {dayjs(d.driveDate).locale('ja').format('M/D(ddd)')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 22, color: '#1f2937' }}>
                        {d.firstEstimatedPickupDatetime}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 1,
                          minWidth: 0,
                          flexWrap: 'nowrap',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 0.75,
                            borderRadius: 0.75,
                            bgcolor: '#f0f9ff',
                            color: '#0369a1',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          <Typography sx={{ fontSize: 14, lineHeight: '19px', whiteSpace: 'nowrap' }}>
                            {d.firstPickupWaypointNumber}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 16,
                            color: '#1e293b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          }}
                          noWrap
                        >
                          {d.firstPickupWaypoint}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 22, color: '#1f2937' }}>
                        {d.lastEstimatedDropoffDatetime}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 1,
                          minWidth: 0,
                          flexWrap: 'nowrap',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 0.75,
                            borderRadius: 0.75,
                            bgcolor: '#f0fdf4',
                            color: '#15803d',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          <Typography sx={{ fontSize: 14, lineHeight: '19px', whiteSpace: 'nowrap' }}>
                            {d.lastDropoffWaypointNumber}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 16,
                            color: '#1e293b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          }}
                          noWrap
                        >
                          {d.lastDropoffWaypoint}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1e293b' }}>{d.areaName}</Typography>
                    </Box>
                  </Box>
                </Box>
              ))}

              {!isSmOrUp && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2,
                    mb: 2,
                  }}
                >
                  <div className="offering-sure-button-container">
                    <button
                      type="button"
                      className="offering-sure-button"
                      disabled={isLoading}
                      onClick={() => {
                        handleBackFromDriveList();
                      }}
                    >
                      {t('offering.back')}
                    </button>
                  </div>
                </Box>
              )}
            </Box>
          ) : offeringList.length > 0 ? (
            <>
              <MbList<OfferingList>
                ref={listRef}
                records={offeringList}
                itemRender={listItemRender}
                keyName="reservationId"
                selectMax={3}
                showSelectMaxLabel={false}
                showDividers={false}
                checkboxPlacement="end"
                listItemButtonSx={{
                  border: '1px solid #cbd5e1',
                  borderRadius: 2,
                  bgcolor: 'white',
                  my: 1.5,
                  px: 2,
                  py: 2,
                  boxShadow: '0px 6px 0px rgba(0,0,0,0.12)',
                }}
              />
              <Box sx={{ height: 30 }} />
            </>
          ) : (
            !isLoading && dataShows && <Typography>{t('offering.zeroMessage')}</Typography>
          )}
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {errorMessage && (
              <Typography variant="caption" sx={{ color: 'red', display: 'block' }}>
                {errorMessage}
              </Typography>
            )}
          </Box>
        </Grid2>
        <Box
          position="fixed"
          bottom="66px"
          left="16px"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Hidden xlDown={scrollTop < 30}>
            <Fab
              variant="circular"
              color="primary"
              onClick={() => {
                scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <KeyboardDoubleArrowUp />
            </Fab>
          </Hidden>
        </Box>
        {!isDriveListView && offeringList && offeringList.length > 0 && (
          <Box
            position="fixed"
            bottom="66px"
            right="16px"
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-end"
            mt={1}
            flexDirection="column"
            gap={1}
          >
            <div className="offering-sure-button-container">
              <button
                type="button"
                className="offering-confirm-button"
                disabled={isLoading}
                onClick={() => {
                  void handleCheck();
                }}
              >
                {t('offering.sureButton')}
              </button>
            </div>
          </Box>
        )}
      </Grid2>

      {isDriveListView && isSmOrUp && (
        <Box position="fixed" bottom="66px" right="16px">
          <div className="offering-sure-button-container">
            <button
              type="button"
              className="offering-sure-button"
              disabled={isLoading}
              onClick={() => {
                handleBackFromDriveList();
              }}
            >
              {t('offering.back')}
            </button>
          </div>
        </Box>
      )}
      {
        <MbDialog
          ref={dialogRef}
          okLabel={t('offering.ok')}
          cancelLabel={t('offering.cancel')}
          onOk={() => {
            popUpDatas.shift();
            setPopUpData(null);
            if (popUpDatas[0]) {
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              setTimeout(async () => {
                setPopUpData(popUpDatas[0]);
                await dialogRef.current?.show();
              }, 100);
            } else {
              // 乗降地点確認画面に遷移
              void navigateWaypointConfirm();
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
              pt: 1,
              px: 4,
              pb: 4,
            }}
          >
            {/* ヘッダー（赤枠・赤文字・admin-update-modal と同様） */}
            <Box
              sx={{
                width: '100%',
                border: '1px solid #ff4b00',
                color: '#ff4b00',
                textAlign: 'center',
                py: 2,
                px: 2,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 24, lineHeight: 1.4 }}>
                {t('offering.communicationItemsTitle')}
              </Typography>
            </Box>
            {/* 注意メッセージ */}
            <Typography
              sx={{
                fontSize: 18,
                color: '#64748b',
                textAlign: 'center',
                lineHeight: 1.4,
                letterSpacing: '0.9px',
              }}
            >
              {t('offering.reservationMsg1')}
            </Typography>
            {/* 詳細行 */}
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                  lineHeight: 1.2,
                }}
              >
                <Typography sx={{ color: '#64748b' }}>{t('offering.pickupDate')}</Typography>
                <Typography sx={{ color: '#111827', fontWeight: 700 }}>
                  {dayjs(popUpData?.requestedAppointmentAt).format('MM月DD日 HH:mm')}
                  {t('offering.space')}
                  {popUpData?.passemgerCount}
                  {t('offering.count')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                }}
              >
                <Typography sx={{ color: '#64748b', fontSize: 14, lineHeight: 1.2 }}>
                  {t('driveDetail.pickupWaypoint')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      backgroundColor: '#f0f9ff',
                      color: '#0369a1',
                      px: 0.5,
                      borderRadius: '4px',
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.6,
                    }}
                  >
                    {popUpData?.pickupWaypointNumber}
                  </Box>
                  <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 14 }}>
                    {popUpData?.pickupWaypointName}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                }}
              >
                <Typography sx={{ color: '#64748b', fontSize: 14, lineHeight: 1.2 }}>
                  {t('driveDetail.dropoffWaypoint')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      backgroundColor: '#f0f9ff',
                      color: '#0369a1',
                      px: 0.5,
                      borderRadius: '4px',
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.6,
                    }}
                  >
                    {popUpData?.dropoffWaypointNumber}
                  </Box>
                  <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 14 }}>
                    {popUpData?.dropoffWaypointName}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: 14,
                }}
              >
                <Typography sx={{ color: '#64748b', fontSize: 14, lineHeight: 1.2 }}>
                  {t('offering.reservationMsg2')}
                </Typography>
                <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
                  {popUpData?.message}
                </Typography>
              </Box>
            </Box>
          </Box>
        </MbDialog>
      }
    </Box>
  );
};
